// src/api/chatApi.js
// Sohbet ve sesli arama uçları (/api/chat). Sunucu sözleşmesi: docs/chat-api.md
import api, { apiGet, invalidateApiCache } from "./api";

const id = (value) => encodeURIComponent(String(value));
const clean = (params) =>
  Object.fromEntries(Object.entries(params || {}).filter(([, value]) => value !== "" && value != null));

export const chatApi = {
  conversations: async (config = {}) =>
    (await apiGet("/api/chat/conversations", { ...config, dedupe: false, cacheTtl: 0 })).data,
  // { type: "management" | "team" | "direct" | "group", user_id?, user_ids?, title?, team_id? }
  open: async (body) => (await api.post("/api/chat/conversations", body)).data,
  conversation: async (conversationId, config = {}) =>
    (await apiGet(`/api/chat/conversations/${id(conversationId)}`, { ...config, cacheTtl: 0 })).data,
  messages: async (conversationId, params = {}, config = {}) =>
    (await apiGet(`/api/chat/conversations/${id(conversationId)}/messages`, { ...config, dedupe: false, cacheTtl: 0, params: clean(params) })).data,
  send: async (conversationId, body) =>
    (await api.post(`/api/chat/conversations/${id(conversationId)}/messages`, body)).data,
  read: async (conversationId) => (await api.post(`/api/chat/conversations/${id(conversationId)}/read`)).data,
  remove: async (conversationId, messageId) =>
    (await api.delete(`/api/chat/conversations/${id(conversationId)}/messages/${id(messageId)}`)).data,
  leave: async (conversationId) => (await api.post(`/api/chat/conversations/${id(conversationId)}/leave`)).data,
  // Sohbeti sil: yalnız kendi görünümünden; çöp kutusuna yazılır.
  deleteConversation: async (conversationId) => (await api.delete(`/api/chat/conversations/${id(conversationId)}`)).data,
  // Kimden mesaj kabul ederim
  preferences: async () => (await apiGet("/api/chat/preferences", { cacheTtl: 0, dedupe: false })).data,
  setPreferences: async (acceptFrom) => (await api.patch("/api/chat/preferences", { accept_from: acceptFrom })).data,
  mute: async (conversationId, muted) => (await api.patch(`/api/chat/conversations/${id(conversationId)}/mute`, { muted })).data,
  directory: async (q = "", config = {}) =>
    (await apiGet("/api/chat/directory", { ...config, cacheTtl: 0, params: clean({ q }) })).data,
  unread: async (config = {}) => (await apiGet("/api/chat/unread-count", { ...config, cacheTtl: 0 })).data,

  // Sesli arama (socket yoksa REST yedeği)
  iceServers: async () => (await apiGet("/api/chat/ice-servers", { cacheTtl: 300000 })).data,
  activeCall: async () => (await apiGet("/api/chat/calls/active", { cacheTtl: 0, dedupe: false })).data,
  startCall: async (body) => (await api.post("/api/chat/calls", body)).data,
  acceptCall: async (callId, body) => (await api.post(`/api/chat/calls/${id(callId)}/accept`, body)).data,
  rejectCall: async (callId) => (await api.post(`/api/chat/calls/${id(callId)}/reject`)).data,
  endCall: async (callId, body = {}) => (await api.post(`/api/chat/calls/${id(callId)}/end`, body)).data,
  sendIce: async (callId, candidate) => (await api.post(`/api/chat/calls/${id(callId)}/ice`, { candidate })).data,

  // Zengin mesajlar
  respond: async (conversationId, messageId, body) =>
    (await api.post(`/api/chat/conversations/${id(conversationId)}/messages/${id(messageId)}/respond`, body)).data,
  resolve: async (conversationId, messageId, body) =>
    (await api.post(`/api/chat/conversations/${id(conversationId)}/messages/${id(messageId)}/resolve`, body)).data,
  uploadAudio: async (blob, { filename = "ses.webm", onProgress } = {}) => {
    const form = new FormData();
    form.append("audio", blob, filename);
    return (await api.post("/api/chat/uploads/audio", form, { timeout: 120000, onUploadProgress: onProgress || undefined })).data;
  },
  venues: async (q = "") => (await apiGet("/api/chat/venues", { cacheTtl: 60000, params: clean({ q }) })).data,
  uploadRecording: async (callId, blob, durationMs) => {
    const form = new FormData();
    form.append("audio", blob, "arama.webm");
    form.append("duration_ms", String(Math.round(durationMs || 0)));
    return (await api.post(`/api/chat/calls/${id(callId)}/recording`, form, { timeout: 300000 })).data;
  },
  // Genel eylem düğmesi çağrısı (api: { method, url, body })
  callAction: async (action) => {
    const method = String(action?.api?.method || "POST").toLowerCase();
    return (await api[method](action.api.url, action.api.body || {})).data;
  },

  invalidate() {
    invalidateApiCache("api/chat");
  },
};

/**
 * Yönetim tarafı: aynı imzalar, /api/admin/chat uçları. ChatPanel `api`
 * özelliğiyle bunu alır; üye tarafıyla aynı bileşen çalışır.
 */
export const adminChatApi = {
  ...chatApi,
  isAdmin: true,
  conversations: async (params = {}, config = {}) =>
    (await apiGet("/api/admin/chat/conversations", { ...config, dedupe: false, cacheTtl: 0, params: clean(params) })).data,
  open: async (body) => (await api.post("/api/admin/chat/conversations", body)).data,
  conversation: async (conversationId, config = {}) =>
    (await apiGet(`/api/admin/chat/conversations/${id(conversationId)}`, { ...config, cacheTtl: 0 })).data,
  messages: async (conversationId, params = {}, config = {}) =>
    (await apiGet(`/api/admin/chat/conversations/${id(conversationId)}/messages`, { ...config, dedupe: false, cacheTtl: 0, params: clean(params) })).data,
  send: async (conversationId, body) =>
    (await api.post(`/api/admin/chat/conversations/${id(conversationId)}/messages`, body)).data,
  read: async (conversationId) => (await api.post(`/api/admin/chat/conversations/${id(conversationId)}/read`)).data,
  respond: async (conversationId, messageId, body) =>
    (await api.post(`/api/admin/chat/conversations/${id(conversationId)}/messages/${id(messageId)}/respond`, body)).data,
  resolve: async (conversationId, messageId, body) =>
    (await api.post(`/api/admin/chat/conversations/${id(conversationId)}/messages/${id(messageId)}/resolve`, body)).data,
  directory: async (q = "", config = {}) =>
    (await apiGet("/api/admin/chat/directory", { ...config, cacheTtl: 0, params: clean({ q }) })).data,
  unread: async (config = {}) => {
    const data = await adminChatApi.conversations({ limit: 1 }, config);
    return { count: Number(data?.unread || 0) };
  },
  deleteConversation: async (conversationId) => (await api.delete(`/api/admin/chat/conversations/${id(conversationId)}`)).data,
  // Yalnız yönetimle yapılan aramalar; ses bağlantısı messages.listen yetkisiyle gelir.
  calls: async (params = {}) => (await apiGet("/api/admin/chat/calls", { cacheTtl: 0, dedupe: false, params: clean(params) })).data,
  stats: async () => (await apiGet("/api/admin/chat/stats", { cacheTtl: 0 })).data,
  // Gelen kutusu özeti: okunmamış / bekleyen talep sayıları, kategori dökümü.
  summary: async () => (await apiGet("/api/admin/chat/summary", { cacheTtl: 0, dedupe: false })).data,
  invalidate() {
    invalidateApiCache("api/chat");
    invalidateApiCache("api/admin/chat");
  },
};

const KNOWN = {
  VALIDATION_ERROR: "Lütfen mesaj alanını kontrol edin.",
  CONVERSATION_NOT_FOUND: "Sohbet bulunamadı.",
  CONVERSATION_FORBIDDEN: "Bu sohbete erişim yetkiniz yok.",
  CONVERSATION_LEFT: "Bu sohbetten ayrıldınız.",
  MESSAGE_NOT_FOUND: "Mesaj bulunamadı.",
  MESSAGE_FORBIDDEN: "Yalnızca kendi mesajınızı silebilirsiniz.",
  USER_NOT_FOUND: "Üye bulunamadı.",
  TEAM_FORBIDDEN: "Yalnızca kendi takımınızın grubunu açabilirsiniz.",
  TEAM_NOT_FOUND: "Takım bulunamadı.",
  CANNOT_LEAVE: "Bu sohbetten ayrılamazsınız.",
  CHAT_FORBIDDEN: "Bu işlem için yetkiniz yok.",
  CALL_NOT_ALLOWED: "Sesli arama yalnızca birebir sohbetlerde yapılabilir.",
  CALL_IN_PROGRESS: "Zaten devam eden bir aramanız var.",
  CALLEE_BUSY: "Aradığınız kişi şu anda başka bir görüşmede.",
  CALL_NOT_FOUND: "Arama bulunamadı.",
  CALL_NOT_RINGING: "Arama artık çalmıyor.",
  CALL_FORBIDDEN: "Bu aramaya erişiminiz yok.",
  CALLEE_UNAVAILABLE: "Şu anda yönetimden aranabilecek kimse yok.",
  OFFER_ALREADY_ANSWERED: "Bu teklif zaten yanıtlanmış.",
  OFFER_FORBIDDEN: "Kendi teklifinizi yanıtlayamazsınız.",
  CANNOT_WRITE: "Bu akışa mesaj yazılamaz.",
  UNSUPPORTED_AUDIO_TYPE: "Bu ses biçimi desteklenmiyor.",
  UPLOAD_UNAVAILABLE: "Ses yükleme deposu yapılandırılmamış.",
  PERMISSION_DENIED: "Bu işlem için yetkiniz yok.",
  CHAT_NOT_ACCEPTED: "Bu üye sizden mesaj kabul etmiyor.",
  CANNOT_DELETE: "Bu sohbet silinemez.",
  RECORDING_NOT_ALLOWED: "Yalnızca yönetimle yapılan aramalar kaydedilir.",
  AUTH_REQUIRED: "Oturumunuz sona ermiş görünüyor. Lütfen yeniden giriş yapın.",
};

export const mapChatError = (error) => {
  const data = error?.response?.data || {};
  return KNOWN[data.code] || data.message || "İşlem tamamlanamadı. Lütfen tekrar deneyin.";
};

export const messageTime = (value) => {
  if (!value) return "";
  try { return new Date(value).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
};

// Liste satırındaki kısa zaman: bugün saat, dün "Dün", bu hafta gün adı, aksi halde tarih.
export const listTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return "";
  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);
  if (diffDays === 0) return messageTime(value);
  if (diffDays === 1) return "Dün";
  if (diffDays < 7) return date.toLocaleDateString("tr-TR", { weekday: "long" });
  return date.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export const dayLabel = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return "";
  const now = new Date();
  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);
  if (diffDays === 0) return "Bugün";
  if (diffDays === 1) return "Dün";
  return date.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
};

export const ACCEPT_FROM_OPTIONS = [
  { key: "everyone", label: "Herkesten", hint: "Tüm takım yöneticileri ve oyuncular sana yazabilir." },
  { key: "teams", label: "Takım yöneticilerinden", hint: "Yalnızca takım yöneticileri." },
  { key: "players", label: "Oyunculardan", hint: "Yalnızca oyuncular." },
  { key: "teammates", label: "Takım arkadaşlarımdan", hint: "Kendi takımının yöneticisi ve oyuncuları." },
];

export const formatDurationMs = (ms) => {
  const total = Math.max(0, Math.round((Number(ms) || 0) / 1000));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

// Konum mesajı için harita bağlantısı: koordinat varsa doğrudan, yoksa adres araması.
export const mapLinkFor = (location) => {
  if (!location) return null;
  if (location.lat != null && location.lng != null) return `https://www.google.com/maps?q=${location.lat},${location.lng}`;
  const q = [location.venue_name || location.label, location.address].filter(Boolean).join(" ");
  return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : null;
};

export const initialsOf = (name) =>
  String(name || "?").split(" ").map((part) => part[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();
